import { z } from "zod";
import { COLUMN_MAX_DIGITS, COLUMN_MAX_KEYS, COLUMN_MAX_PLACES } from "./column.ts";
import { LABEL_TEX_MAX } from "./label-tex-limits.ts";
import { SoftPageLink } from "./page-link.ts";
import {
  TRANSLATE_ANSWER_MAX,
  TRANSLATE_FOCUS_MAX,
  TRANSLATE_ITEM_MAX,
  TRANSLATE_MAX_ITEMS,
} from "./translate.ts";

/**
 * Второй слой слайда: интерактивные элементы.
 *
 * Слой объявлен в шапке slides.ts с самого начала («три слоя в одной системе
 * координат»), но до сих пор был пуст. Здесь описано то, что в нём живёт.
 *
 * ⚠️ Главное решение всего слоя, и оно физическое, а не по соглашению:
 * **здесь нет и не может быть ответов**. Тип `ExerciseElement` это то, что
 * видит ученик, и у него попросту нет полей, куда ответ поместился бы. То,
 * что набрал преподаватель (`ExerciseSource`), и эталон (`ExerciseKey`) живут
 * в другом модуле, `@edu/shared/exercises`, который НЕ реэкспортируется отсюда
 * и в браузер ученика не попадает ни одним импортом.
 *
 * Разделение нужно потому, что у четырёх видов ответ живёт прямо в условии:
 * шаблон «Вчера я {{ходил}} в кино» содержит ответ буквально. Обфускация и
 * хеши не спасают (у выбора из четырёх вариантов пространство ответов равно
 * четырём), поэтому спасает только то, что ответа в документе нет вовсе.
 * Превращает источник в элемент компиляция, см. exercises-authoring.ts.
 *
 * Здесь же лежат ответы ученика (`Answer`): они попадают и в Y.Doc урока, и в
 * снимок архива, то есть читают их все.
 *
 * ⚠️ Геометрия новой коробки (размер по виду, диагональ, прижатие к краям) живёт
 * в соседнем `./exercise-frame.ts`, а не здесь: она читает из схем ровно один
 * тип и ни одного значения, зато читает размер слайда, которого здесь нет.
 */

/**
 * Потолок на число элементов слоя 2 на одном слайде.
 *
 * Потолок здравого смысла, а не базы: два десятка заданий на одном экране это
 * уже не слайд, а страница учебника. Живёт рядом с `PLAN_MAX_SLIDES` и по той
 * же причине: второй такой константы быть не должно.
 */
export const SLIDE_MAX_EXERCISES = 24;

/**
 * Потолки вида `blanks` и свободного ответа.
 *
 * ⚠️ Константы, а не литералы в схеме, потому что у каждой есть ВТОРОЙ читатель,
 * и разъехаться им нельзя ни одной цифрой:
 *
 *   BLANKS_MAX_SEGMENTS      компиляция шаблона: больше этого числа сегментов
 *                            это отказ `template_too_long`, а не элемент,
 *                            который схема потом молча отвергнет
 *   BLANKS_SEGMENT_TEXT_MAX  она же: длинный кусок текста режется на несколько
 *                            сегментов, а не уезжает одним неразбираемым
 *   ANSWER_INPUT_MAX         сборка снимка архива: ответ длиннее потолка
 *                            обрезается, потому что иначе не разберётся ВЕСЬ
 *                            снимок урока, а не одно поле
 *   ANSWER_BLANK_MAX         то же самое для значения одного пропуска
 *   ANSWER_NUMBER_MAX        потолок поля ввода числа у ученика. Стоит ЗДЕСЬ,
 *                            а не у виджета, потому что режет по нему клиент,
 *                            а отвергает схема: разойдясь, они дают ответ,
 *                            который записан, но не читается, и тогда кнопка
 *                            «Проверить» молчит навсегда и без сообщения
 *
 * Литералы в двух местах дали бы худший вид расхождения: тихий, отложенный и
 * заметный только на настоящем уроке с длинным текстом.
 */
export const BLANKS_MAX_SEGMENTS = 60;
export const BLANKS_SEGMENT_TEXT_MAX = 1000;
export const ANSWER_INPUT_MAX = 20000;
export const ANSWER_BLANK_MAX = 200;
export const ANSWER_NUMBER_MAX = 64;

/**
 * Потолки ЭТАЛОНА у пропусков и свободного ввода: сколько принятых строк и
 * какой длины.
 *
 * ⚠️ Константы по той же причине, что у соседей выше, и второй читатель у них
 * ровно один и тот же: **редактор заготовки**. Схема эталона (`ExerciseKey` в
 * `./checking-answer.ts`) эти потолки уже требует, а компиляция шаблона их не
 * стережёт вовсе: она собирает эталон из того, что набрал человек, и отдаёт его
 * успехом. Дальше такой эталон записывается, а на следующем чтении молча
 * пропадает вместе с заданием, и заметит это ученик.
 *
 * Поэтому потолки обязан держать редактор, на вводе, и держать он их обязан по
 * ТОМУ ЖЕ числу, что стоит в схеме. Литерал в панели свойств и литерал в схеме
 * это ровно тот тихий разъезд, ради которого заведены константы выше.
 *
 * ⚠️ `BLANK_ACCEPT_MAX` намеренно равен `ANSWER_BLANK_MAX`: принятая строка
 * длиннее того, что ученик способен набрать в поле, не совпала бы никогда.
 * Числа при этом разные по смыслу (эталон и ответ), поэтому и константы разные:
 * однажды их разведут, и тогда общая константа солгала бы обоим.
 */
export const BLANK_ACCEPT_MAX = 200;
export const BLANK_ACCEPT_COUNT_MAX = 20;
export const INPUT_ACCEPT_MAX = 2000;
export const INPUT_ACCEPT_COUNT_MAX = 20;

/**
 * Потолки того, что набирает автор в панели свойств: условие задания и шаблон
 * пропусков.
 *
 * ⚠️ Второй читатель тот же, что у блока выше, и причина та же: у каждого поля
 * стоит `maxLength`, и стоит он затем, чтобы набор упирался в потолок ВИДИМО.
 * Без него первый лишний знак роняет `safeParse` в панели, и правка перестаёт
 * доезжать молча: человек печатает, а на слайде ничего не меняется.
 *
 * `EXERCISE_PROMPT_MAX` один на все виды, у которых есть условие (выбор,
 * свободный ввод, число, выражение), потому что поле у них буквально одно и то
 * же. Разведи их однажды по видам, и разводить придётся вместе со схемой, а не
 * вместо неё.
 */
export const EXERCISE_PROMPT_MAX = 1000;
export const BLANKS_TEMPLATE_MAX = 4000;

/**
 * Потолки записи решения: строк и знаков в строке (2.1
 * `docs/room-math-input-design.md`). 30 по 120 это 3600, в пределах потолка
 * `text`.
 *
 * ⚠️ Второй читатель тот же, что у соседей выше: строчный редактор, и держит он
 * их ДО записи (не даёт набрать 121-й знак и не открывает 31-ю строку). Элемент,
 * не прошедший схему, молча пропадает у всех, включая автора и снимок архива, то
 * есть лишний знак стёр бы всю запись, а не один этот знак.
 */
export const DERIVATION_MAX_LINES = 30;
export const DERIVATION_MAX_LINE = 120;
/**
 * Кегль записи решения по умолчанию. Читателей два, схема ниже и высота новой
 * коробки (`./exercise-frame.ts`), и число у них обязано быть одним: коробка по
 * другому кеглю была бы не по своим строкам.
 */
export const DERIVATION_SIZE_DEFAULT = 24;

/**
 * Место элемента на слайде, в логических единицах 1280x720.
 *
 * Никаких вычислений при отрисовке: узел слоя 2 имеет ровно логический размер
 * и уменьшается трансформом, поэтому `x`, `y`, `w`, `h` уезжают в
 * `position: absolute` как есть. Совпадение позиции между десктопом и
 * телефоном получается по построению, а не подгонкой.
 */
export const ExerciseFrame = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
});
export type ExerciseFrame = z.infer<typeof ExerciseFrame>;

/**
 * Единица с собственным идентификатором: вариант ответа, токен, столбец.
 *
 * ⚠️ `id` не короче восьми символов и берётся из `crypto.getRandomValues`, а не
 * из позиции в источнике. Без этого перемешивание не стоит ничего: ученик
 * сортирует токены по id и получает правильный порядок целиком, не глядя на
 * экран. Минимальная длина стоит здесь схемой затем, чтобы позиционный `t1`
 * не прошёл разбор даже случайно.
 *
 * Текст может нести математику в долларах, как и `text`.
 */
export const ExerciseToken = z.object({
  id: z.string().min(8),
  text: z.string().max(300),
});
export type ExerciseToken = z.infer<typeof ExerciseToken>;

/**
 * Спецификация графика. Здесь, а не в модуле авторинга: она не секретна,
 * её видит ученик вместе с самим графиком, и на неё ссылается `ExerciseElement`.
 */
export const PlotSpec = z.object({
  /** Выражения. Исходник, до нормализации омоглифов и псевдонимов. */
  exprs: z.array(z.string().max(200)).min(1).max(4),
  /** Имена переменных. Первая это ось абсцисс. */
  vars: z.array(z.string().max(3)).min(1).max(2).default(["x"]),
  /** Окно. null означает «подобрать по выборке». */
  window: z
    .object({
      xMin: z.number(),
      xMax: z.number(),
      yMin: z.number(),
      yMax: z.number(),
    })
    .nullable()
    .default(null),
  grid: z.boolean().default(true),
  axisLabels: z.boolean().default(true),
  /** Размер в логических единицах слайда. */
  w: z.number().int().min(160).max(1280).default(640),
  h: z.number().int().min(120).max(720).default(400),
});
export type PlotSpec = z.infer<typeof PlotSpec>;

/**
 * Рубеж прав у заказа на рисование: ЗАГОТОВКА либо УРОК.
 *
 * Глобальной роли «преподаватель» в схеме нет вовсе: преподаватель это тот, чьи
 * заготовки и ученики. Поэтому рубежей ровно два, и оба про владение: своя
 * заготовка (отказ `404 plan_not_found`) либо свой урок с нуждой `manage`
 * (отказ `404 lesson_not_found`). Правило проекта записано в
 * `apps/api/src/http.ts`: отказ по правам это 404, а не 403.
 *
 * ⚠️ **Юнион, а не «`planId` необязателен»** (`docs/math-drawing-design.md`,
 * 3.1). Необязательный рубеж однажды не передадут, и ручка окажется открытой
 * любой живой сессии. Юнион не даёт собрать запрос без рубежа вовсе, и проверку
 * делает компилятор, а не внимательность.
 */
export const PlotScope = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("plan"), planId: z.string().uuid() }),
  z.object({ kind: z.literal("lesson"), lessonId: z.string().uuid() }),
]);
export type PlotScope = z.infer<typeof PlotScope>;

/**
 * Заказ на рисование графика. Клиент собирает ИМЕННО ЭТО; старую форму читает
 * только сервер, и только пока не выкачен клиент (см. `parseCreatePlot`).
 */
export const CreatePlotRequest = z.object({
  scope: PlotScope,
  spec: PlotSpec,
  /**
   * Запрос дозаливки подписи (`docs/graph-formula-label-design.md`, 3.3): на
   * промахе кэша сервер НЕ рисует, а отвечает `409 plot_not_cached`. Иначе
   * дозаливка рисовала бы заново, тратила квоту и заводила в материалах файл,
   * к элементу не относящийся. Старая сборка API поле отбросит и нарисует, это
   * цена отката, не более.
   */
  probe: z.boolean().default(false),
});
export type CreatePlotRequest = z.infer<typeof CreatePlotRequest>;

/**
 * Разбор тела ручки, и он ЕДИНСТВЕННЫЙ у сервера: обе формы приводятся к одной
 * здесь, а не двумя ветками в обработчике.
 *
 * ⚠️ **Старая форма (`planId` на верхнем уровне) понимается НАРОЧНО, и это
 * временно.** Контракт переезжает в три шага, а не в один
 * (`docs/math-drawing-design.md`, 3.1): живое превью редактора заготовки шлёт
 * запрос на каждый набранный символ, а открытая вкладка сама не
 * перезагружается (5.1 CLAUDE.md). Замена формы одним коммитом дала бы
 * `400 invalid_body` на каждое нажатие клавиши всем, кто прямо сейчас собирает
 * заготовку.
 *
 * ⚠️ **Эта ветка снимается очередью «А1-хвост»**, следующим коммитом после
 * выкатки клиента, который шлёт `scope`. Снимать её вместе с этой функцией:
 * без старой формы разбор это одна строка `CreatePlotRequest.safeParse`.
 *
 * Рубеж у старой формы всегда `plan`: уроку она не была известна вовсе.
 */
export function parseCreatePlot(body: unknown): z.ZodSafeParseResult<CreatePlotRequest> {
  return CreatePlotRequest.safeParse(withScope(body));
}

/**
 * Поднять старый `planId` в рубеж. Всё остальное отдаётся как пришло, чтобы об
 * ошибках в теле говорила схема, а не эта функция: два источника сообщений про
 * одно тело это два разных текста на экране.
 */
function withScope(body: unknown): unknown {
  if (typeof body !== "object" || body === null) return body;
  const bag = body as Record<string, unknown>;
  const planId = bag["planId"];
  if ("scope" in bag || typeof planId !== "string") return body;
  return { ...bag, scope: { kind: "plan", planId } };
}

/**
 * Нарисованный график: обычный файл в материалах преподавателя.
 *
 * Именно `fileId`, а не адрес: адрес живёт минуты, а слайд лежит годами. Та же
 * причина, по которой `fileId` лежит в подложке слайда.
 */
export const PlotResponse = z.object({
  fileId: z.string().uuid(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Попали в кэш: график с такой спецификацией уже был нарисован. */
  cached: z.boolean().default(false),
  /**
   * Подпись формулой по выражениям в порядке `spec.exprs`, собранная сервером
   * из того же дерева, по которому нарисована картинка. `null` у выражения это
   * отказ печати; `null` целиком это «сервер сейчас не посчитал» (процесс спал
   * или занят), а не «подписи нет».
   *
   * ⚠️ **Потолки здесь СТРОГИЕ, а в элементе мягкие** (3.4 проекта). Ответ
   * собирает наш же сервер, и ответ за потолком это наша ошибка, которую ловит
   * `PlotResponse.parse` на сервере. Умолчание `null` это старый сервер.
   */
  labelTex: z
    .array(z.string().max(LABEL_TEX_MAX).nullable())
    .max(4)
    .nullable()
    .default(null),
  /** Версия вида подписи (`LABEL_TEX_VERSION`), `null` без подписи. */
  labelTexV: z.number().int().positive().nullable().default(null),
});
export type PlotResponse = z.infer<typeof PlotResponse>;

/**
 * Подпись графика в ЭЛЕМЕНТЕ и в ИСТОЧНИКЕ заготовки: те же потолки, что в
 * ответе, но МЯГКО.
 *
 * ⚠️ **`.catch(null)` обязателен, и это не небрежность.** Элемент читается из
 * документа урока и из заготовки, а `readExercises` выбрасывает элемент, не
 * прошедший схему. Строгая подпись означала бы: битая подпись (запись старой
 * сборки, ручная правка, завтрашний потолок меньше сегодняшнего) снимает с
 * доски ВЕСЬ график. Здесь битая подпись становится «подписи нет», а график
 * остаётся.
 */
export const GraphLabelTex = z
  .array(z.string().max(LABEL_TEX_MAX).nullable())
  .max(4)
  .nullable()
  .default(null)
  .catch(null);
export const GraphLabelTexV = z.number().int().positive().nullable().default(null).catch(null);

const base = {
  /**
   * Идентификатор элемента на слайде. Случайный, как id слайда, и `text`, а не
   * uuid: заводит его клиент, в том числе на телефоне, открытом по http из
   * локальной сети, где `crypto.randomUUID` недоступен.
   */
  id: z.string().min(1),
  frame: ExerciseFrame,
  /**
   * Страница, с которой задание извлечено (решение 4
   * `docs/scan-extraction-design.md`). Мягко: разбор у `SoftPageLink`.
   *
   * ⚠️ В `base`, то есть у всех двенадцати видов, и заодно у записи решения,
   * столбика и перевода: их схема служит и источником заготовки, поэтому в
   * их ИСТОЧНИКЕ привязка тоже мягкая, а не строгая, как у `sourceBase`.
   * Извлечение этих видов не ставит, и строгость там охранять нечего.
   */
  page: SoftPageLink,
};

/**
 * Задание без автопроверки (решение 22 `docs/scan-extraction-design.md`):
 * ответ стёрт, потому что звучит в записи или стоит на другой странице, и
 * проверяет его преподаватель. Только у `choice`, `blanks` и `input`.
 *
 * Мягко, как `page`: будущее значение у старой вкладки не должно снимать
 * задание с доски. Ответа поле не несёт, это признак, а не эталон.
 */
const SoftReview = z.literal("teacher").optional().catch(undefined);

/**
 * Запись решения: строки ровно так, как их набрал преподаватель, не TeX. Вид
 * строки считает `schoolToTex` при отрисовке (2.1 и 2.2
 * `docs/room-math-input-design.md`).
 *
 * Отдельной схемой, а не строкой юниона, потому что источник заготовки у этого
 * вида равен элементу (эталона нет, прятать нечего), и `ExerciseSource` берёт
 * ЭТУ ЖЕ схему. Две копии одних потолков однажды разъехались бы.
 */
export const DerivationElement = z.object({
  ...base,
  kind: z.literal("derivation"),
  lines: z.array(z.string().max(DERIVATION_MAX_LINE)).max(DERIVATION_MAX_LINES),
  /** Кегль в логических единицах слайда, диапазон и умолчание как у `text`. */
  size: z.number().int().min(10).max(96).default(DERIVATION_SIZE_DEFAULT),
});

/**
 * Число условия столбика: неотрицательное десятичное строкой, запятая или
 * точка, не больше `COLUMN_MAX_DIGITS` цифр.
 *
 * ⚠️ Правило обязано быть не мягче `columnLayout`: элемент, пропущенный схемой,
 * строит сетку всегда (кроме `too_big`, разбор у `ColumnElement`), иначе на
 * слайде стоит коробка, которую нечем нарисовать. Формат тот же, что у
 * `parseDec`, а цифры считаются как набраны, вместе с ведущими нулями. Это чуть
 * строже раскладки (там `007` одна цифра), и настоящему элементу не стоит
 * ничего: поле ввода отдаёт число без ведущих нулей (`parseColumnInput`).
 */
const ColumnNumber = z
  .string()
  .regex(/^\d+(?:[.,]\d+)?$/)
  .refine((s) => s.replace(/[.,]/, "").length <= COLUMN_MAX_DIGITS);

/**
 * Столбик: условие, которое ставит преподаватель. Сетки в элементе нет, её
 * каждый раз строит `columnLayout(op, a, b, places)`, а цифры лежат в ответе
 * (2.3 `docs/room-math-input-design.md`). `places` значим только у деления.
 *
 * ⚠️ Деление на ноль отвергается здесь, а не только полем ввода: иначе элемент
 * проходит схему и не строит сетки. Отказ `too_big` схема НЕ повторяет
 * намеренно. Он зависит от раскладки, а раскладку ещё будут править по ответам
 * Влада (раздел 9 проекта): схема, спросившая раскладку, уронила бы уже
 * поставленный столбик в день правки, и элемент молча пропал бы у всех и из
 * архива. Такой столбик ставить неоткуда (поле ввода его не пустит), а
 * пришедший извне получает минимальную коробку (`columnBoxSize`).
 *
 * Источник заготовки равен элементу, поэтому схема отдельная, как у записи
 * решения.
 */
export const ColumnElement = z
  .object({
    ...base,
    kind: z.literal("column"),
    op: z.enum(["add", "sub", "mul", "div"]),
    a: ColumnNumber,
    b: ColumnNumber,
    places: z.number().int().min(0).max(COLUMN_MAX_PLACES),
    /**
     * Версия раскладки, по которой поставлен столбик.
     *
     * ⚠️ Сетка не хранится, а вычисляется из условия И КОДА раскладки, а
     * раскладка заведомо поменяется после ответов преподавателя (вопросы 1-3
     * раздела 9 проекта). Цифры ответа лежат по ключам клеток (`r3c5`), и без
     * версии правка раскладки сдвинула бы их под другие клетки во всех старых
     * уроках и в архиве. С версией старый столбик строится своей раскладкой.
     * Новая версия это новый литерал здесь, и юнион, а не замена.
     *
     * Версии 1 и 2 делят геометрию и ключи ответа (`r3c4,` у обеих одна и та
     * же запятая), а номер различает ввод, показ и суждение о запятой: у 1 она
     * метка на углу клетки, у 2 своя узкая клетка
     * (docs/column-decimal-cell-design.md). ⚠️ Литералы повторяют
     * `COLUMN_LAYOUTS`, совпадение держит тест.
     */
    layout: z.union([z.literal(1), z.literal(2)]),
  })
  .refine((el) => el.op !== "div" || /[1-9]/.test(el.b), { path: ["b"] });

/**
 * Перевод предложений: русские предложения, под каждым своё поле ответа (2.1
 * `docs/translate-exercise-design.md`). Эталона у перевода нет, проверяет
 * человек, поэтому источник заготовки равен элементу и схема отдельная, как у
 * записи решения.
 *
 * `lang` это язык ОТВЕТА: подпись «Переведите на английский» и атрибут `lang`
 * у поля. Предмет урока до экрана ученика не доезжает, поэтому язык лежит в
 * самом задании (1.6 проекта).
 *
 * ⚠️ У предложения свой `id`, а не номер строки: ответ лежит по id, и правка
 * одной строки заготовки не должна сдвигать смысл соседних ответов.
 */
export const TranslateElement = z.object({
  ...base,
  kind: z.literal("translate"),
  lang: z.enum(["en", "de"]),
  /**
   * «Что тренируем» (1.11 `docs/translate-ai-check-design.md`): тренируемый
   * пункт грамматики словами преподавателя. Необязательное, и старые задания
   * без него разбираются как были. Едет модели, проверяющей черновик: перевод,
   * обходящий названный пункт, для неё неверен.
   */
  focus: z.string().max(TRANSLATE_FOCUS_MAX).optional(),
  items: z
    .array(
      z.object({
        // Метка вида лежит в той же карте ответа под этим ключом: предложение
        // с таким id делило бы ключ с меткой
        id: z
          .string()
          .min(1)
          .refine((id) => id !== COLUMN_MARK_KEY),
        text: z.string().min(1).max(TRANSLATE_ITEM_MAX),
      }),
    )
    .max(TRANSLATE_MAX_ITEMS)
    // Ответ лежит по id: два предложения с одним id делили бы один ответ
    .refine((items) => new Set(items.map((item) => item.id)).size === items.length),
});

/**
 * Что видит ученик. Двенадцать видов, дискриминированный юнион.
 *
 * ⚠️ Неизвестный `kind` роняет разбор ВСЕГО массива, а не одного элемента,
 * ровно как у подложки. Отсюда то же правило выкатки: api и collab узнают
 * новый вид раньше, чем web получает кнопку, которая его пишет. На чтении
 * неразобранный элемент пропускается поэлементно (см. `readSlideExercises` и
 * `buildSnapshot`), но на записи пропущенное стирается, и это лечится только
 * порядком релизов.
 *
 * Почему `text` один вид, а не «текст» и «формула» отдельно. Условие
 * математической задачи почти всегда это текст с формулой внутри: «Решите
 * уравнение $x^2-5x+6=0$ и найдите $x_1+x_2$». Два вида заставили бы ставить
 * три коробки и выравнивать их руками, а при смене масштаба они разъедутся.
 * Палитра при этом показывает две кнопки: палитра это интерфейс, а не схема.
 *
 * Почему аудирования нет отдельным видом: это уже написанный `SlideMedia` плюс
 * любой из отвечаемых видов. Отдельный вид дал бы вторую ветку
 * воспроизведения, и она однажды разъедется с первой.
 */
export const ExerciseElement = z.discriminatedUnion("kind", [
  z.object({
    ...base,
    kind: z.literal("text"),
    text: z.string().max(4000),
    align: z.enum(["left", "center"]).default("left"),
    /** Кегль в логических единицах слайда, а не в CSS-пикселях экрана. */
    size: z.number().int().min(10).max(96).default(24),
  }),
  z.object({
    ...base,
    kind: z.literal("graph"),
    spec: PlotSpec,
    /** SVG, нарисованный сервером. null, пока не нарисован. */
    fileId: z.string().uuid().nullable(),
    /**
     * Подпись формулой от ТОГО ЖЕ ответа, что `fileId`, и её версия. Пишутся
     * одним вызовом с картинкой; у старых элементов их нет, и тогда `null`.
     */
    labelTex: GraphLabelTex,
    labelTexV: GraphLabelTexV,
  }),

  // Отвечаемые виды. Ни в одном нет поля, куда поместился бы ответ, и это
  // проверяемое утверждение, а не обещание: смотреть в ExerciseKey
  z.object({
    ...base,
    kind: z.literal("choice"),
    prompt: z.string().max(1000),
    options: z.array(ExerciseToken).min(2).max(10),
    multiple: z.boolean().default(false),
    /**
     * Варианты не перемешаны и рисуются с номерами по позиции (решение 13
     * docs/exercise-extraction-design.md). Только `true` или отсутствие поля.
     * ⚠️ Без этой строки снимок доски (`apps/collab/src/snapshot.ts`) срезал
     * бы поле молча, и номера пропали бы из архива.
     */
    ordered: z.literal(true).optional(),
    review: SoftReview,
  }),
  z.object({
    ...base,
    kind: z.literal("blanks"),
    /** Куски текста вперемешку с пропусками: `{t}` это текст, `{b}` это id пропуска. */
    segments: z
      .array(
        z.union([
          z.object({ t: z.string().max(BLANKS_SEGMENT_TEXT_MAX) }),
          z.object({ b: z.string().min(8) }),
        ]),
      )
      .max(BLANKS_MAX_SEGMENTS),
    review: SoftReview,
  }),
  z.object({
    ...base,
    kind: z.literal("match"),
    left: z.array(ExerciseToken).min(2).max(12),
    right: z.array(ExerciseToken).min(2).max(12),
  }),
  z.object({
    ...base,
    kind: z.literal("order"),
    tokens: z.array(ExerciseToken).min(2).max(20),
  }),
  z.object({
    ...base,
    kind: z.literal("input"),
    prompt: z.string().max(1000),
    multiline: z.boolean().default(false),
    review: SoftReview,
  }),
  z.object({
    ...base,
    kind: z.literal("number"),
    prompt: z.string().max(1000),
    unit: z.string().max(16).nullable().default(null),
  }),
  z.object({
    ...base,
    kind: z.literal("expr"),
    prompt: z.string().max(1000),
    vars: z.array(z.string().max(3)).max(4).default(["x"]),
  }),

  // Математика урока: без эталона, разбор у каждой схемы выше
  DerivationElement,
  ColumnElement,

  // Без автопроверки: эталон пустой, ответ читает преподаватель
  TranslateElement,
]);
export type ExerciseElement = z.infer<typeof ExerciseElement>;

export type ExerciseKind = ExerciseElement["kind"];

/**
 * Два признака вида вместо одного «спрашивает ли он о чём-нибудь ученика».
 *
 * Один признак отвечал на два разных вопроса сразу: ловит ли виджет нажатия
 * ученика и нужен ли заданию эталон. Столбик их разводит (нажатия ловит, а
 * эталона у него нет: ответ выводится из чисел на экране), разбор в разделе 2.4
 * `docs/room-math-input-design.md`.
 *
 * ⚠️ **Таблица по ВСЕМ видам, а не список исключений.** Прежний признак был
 * задан от обратного, чтобы новый вид не попал в урок без эталона молча. Теперь
 * это делает компилятор: вид, дописанный в `ExerciseElement`, не соберётся, пока
 * ему не решили оба признака. Забытое решение дорого с обеих сторон: задание без
 * эталона это вечный `409 key_missing` у ученика посреди занятия, а виджет, не
 * ловящий нажатий, отдаёт их холсту, и ученик рисует точку вместо ответа.
 *
 * ⚠️ Вид может прийти и НЕ из таблицы: заготовка из базы разбирается приведением
 * типа, а не схемой (`repo/plans.ts:200-201`), и после отката API на старую
 * сборку там окажется вид, которого эта сборка не знает. Тогда ответ прежнего
 * правила («да» обоим): задание упадёт в компиляции с понятным отказом, а не в
 * `TypeError` посреди выдачи домашки.
 */
const KIND_TRAITS: Record<ExerciseKind, { catchesPointer: boolean; needsKey: boolean }> = {
  text: { catchesPointer: false, needsKey: false },
  graph: { catchesPointer: false, needsKey: false },
  choice: { catchesPointer: true, needsKey: true },
  blanks: { catchesPointer: true, needsKey: true },
  match: { catchesPointer: true, needsKey: true },
  order: { catchesPointer: true, needsKey: true },
  input: { catchesPointer: true, needsKey: true },
  number: { catchesPointer: true, needsKey: true },
  expr: { catchesPointer: true, needsKey: true },
  // Правит только преподаватель, своим редактором; ученику ловить нечего
  derivation: { catchesPointer: false, needsKey: false },
  // Цифры ученика ловит, а эталон выводится из чисел условия на экране
  column: { catchesPointer: true, needsKey: false },
  // Эталон есть, но пустой: «проверяет человек» (1.2 docs/translate-exercise-design.md).
  // Без него обе компиляции выбросили бы задание из урока и домашки
  translate: { catchesPointer: true, needsKey: true },
};

/**
 * Виджет этого вида ловит нажатия ученика: `data-answerable` и
 * `pointer-events` у коробки, островок «рисовать поверх», строка о том, почему
 * ответить нельзя.
 */
export function catchesPointer(kind: ExerciseKind): boolean {
  return KIND_TRAITS[kind]?.catchesPointer ?? true;
}

/**
 * У задания этого вида обязан быть эталон: оно идёт на проверку `/check` и в
 * домашку, а без эталона урок из заготовки не создаётся (`exercise_key_missing`).
 */
export function needsKey(kind: ExerciseKind): boolean {
  return KIND_TRAITS[kind]?.needsKey ?? true;
}

/**
 * Значение клетки столбика: ровно один знак. Цифра, запятая при клетке, точка
 * заёма в ряду «в уме» или минус в первой клетке итога (2.3
 * `docs/room-math-input-design.md`).
 */
const COLUMN_CELL_VALUE = /^[0-9,.-]$/;

/**
 * Ответ ученика.
 *
 * Числовой ответ хранится СТРОКОЙ, и это не небрежность. Ученик пишет `3,14`,
 * `1/3`, `2·10^3`, `≈5`, или ещё ничего не дописал. Число не умеет представить
 * ни запятую, ни пустоту, ни промежуточное состояние набора, а разбор это дело
 * проверки, а не хранения. Заодно в архиве видно, что человек написал, а не то,
 * во что мы это разобрали.
 */
export const Answer = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("choice"), picked: z.array(z.string()).max(10) }),
  z.object({ kind: z.literal("match"), pairs: z.record(z.string(), z.string()) }),
  z.object({ kind: z.literal("order"), order: z.array(z.string()).max(20) }),
  z.object({ kind: z.literal("number"), value: z.string().max(ANSWER_NUMBER_MAX) }),
  z.object({ kind: z.literal("expr"), value: z.string().max(200) }),
  /**
   * ⚠️ Два варианта ниже существуют ТОЛЬКО в снимке архива.
   *
   * В живом документе свободный ввод это `Y.Text`, а пропуски это
   * `Y.Map<blankId, string>`: перезаписывать абзац в 500 знаков целиком на
   * каждое нажатие значит класть надгробие в 500 байт на нажатие, а двенадцать
   * пропусков в одном объекте это 200-300 байт на каждое нажатие вместо
   * единиц. `Answer.safeParse` вложенные типы отвергнет, поэтому сборка снимка
   * приводит их к плоскому виду ЯВНОЙ веткой. Без неё все свободные ответы
   * ученика молча выпали бы из архива, а это самый ценный для преподавателя
   * вид ответа.
   */
  z.object({ kind: z.literal("input"), value: z.string().max(ANSWER_INPUT_MAX) }),
  z.object({
    kind: z.literal("blanks"),
    values: z.record(z.string(), z.string().max(ANSWER_BLANK_MAX)),
  }),
  /**
   * ⚠️ Столбик тоже плоский ТОЛЬКО в снимке архива. В живом документе это
   * `Y.Map` по клетке на ключ, как у пропусков, и по той же причине: цифра
   * пишется сразу, и объект целиком на каждую цифру это надгробие размером с
   * сетку. Плюс в паре 1:1 поздний пишущий затирал бы раннего целиком. По типу
   * Yjs такую карту от пропусков не отличить, поэтому в ней лежит метка
   * `COLUMN_MARK_KEY` со значением `COLUMN_MARK`, а в `cells` метки нет.
   * Ключи клеток описаны в `./column-grid.ts`, приведение к плоскому виду
   * делает `columnCellsOf`.
   *
   * Потолки обязательны: без них один лишний ключ или длинное значение
   * валили бы разбор выдачи архива целиком (`BoardSnapshotResponse`).
   */
  z.object({
    kind: z.literal("column"),
    cells: z
      .record(z.string(), z.string().regex(COLUMN_CELL_VALUE))
      .refine((cells) => Object.keys(cells).length <= COLUMN_MAX_KEYS),
  }),
  /**
   * ⚠️ Перевод плоский в снимке архива и в попытке домашки. В живом документе
   * это `Y.Map` по id предложения со значением `Y.Text` и меткой вида, как у
   * столбика; приведение делает `translateLinesOf`.
   *
   * Потолок числа ключей обязателен по той же причине, что у столбика: лишний
   * ключ ронял бы разбор всей выдачи архива.
   */
  z.object({
    kind: z.literal("translate"),
    lines: z
      .record(
        // Ключ метки это не предложение: ответ с ним это недочищенная живая карта
        z.string().refine((key) => key !== COLUMN_MARK_KEY),
        z.string().max(TRANSLATE_ANSWER_MAX),
      )
      .refine((lines) => Object.keys(lines).length <= TRANSLATE_MAX_ITEMS),
  }),
]);
export type Answer = z.infer<typeof Answer>;

export type ColumnAnswer = Extract<Answer, { kind: "column" }>;

/**
 * Метка вида у ответа столбика в живом документе: ключ и значение (2.3
 * `docs/room-math-input-design.md`). Карта ответа без метки это пропуски.
 */
export const COLUMN_MARK_KEY = "#";
export const COLUMN_MARK = "column";
/**
 * Метка перевода под тем же ключом `COLUMN_MARK_KEY` (2.3
 * `docs/translate-exercise-design.md`). Без неё карта перевода читалась бы как
 * пропуски и уезжала бы в архив обрезанной до `ANSWER_BLANK_MAX`.
 */
export const TRANSLATE_MARK = "translate";

/**
 * Клетки столбика из записей живой карты ответа. `null` это «ответа нет».
 *
 * ⚠️ Читателей ДВА, и оба зовут эту функцию: `answer-read.ts` у веба и
 * `answersOf` в collab. Сами они держат только проверку метки, потому что для
 * неё нужен `yjs` значением, а сюда он не едет. Приведение к плоскому виду при
 * этом одно: разъехавшись, архив и живой экран показали бы разное на одной
 * сетке.
 *
 * Клетка со значением не из схемы (пустая строка, два знака, не строка) это не
 * ответ, а чужая или недописанная запись, и она пропускается, как пропуск из
 * пробелов. Сетка без клеток это отсутствие ответа, а не пустой ответ (иначе
 * преподавателю «Сбросить» под пустой сеткой, а архиву ответ, которого не
 * давали). Больше `COLUMN_MAX_KEYS` клеток наша сетка не набирает
 * (`keyBudget`), и такая карта тоже читается как «ответа нет»: обрезать нечем
 * одинаково у двух читателей, порядок ключей `Y.Map` у клиентов разный.
 */
export function columnCellsOf(
  entries: Iterable<readonly [string, unknown]>,
): Record<string, string> | null {
  const cells: Record<string, string> = {};
  for (const [key, raw] of entries) {
    if (key === COLUMN_MARK_KEY) continue;
    if (typeof raw !== "string" || !COLUMN_CELL_VALUE.test(raw)) continue;
    cells[key] = raw;
  }
  const count = Object.keys(cells).length;
  return count === 0 || count > COLUMN_MAX_KEYS ? null : cells;
}

/**
 * Ответ перевода из записей живой карты ответа. `null` это «ответа нет».
 *
 * ⚠️ Читателей ТРИ (`answer-read.ts` и `slides.ts` у веба, `answersOf` в
 * collab), и все зовут эту функцию, держа у себя только проверку метки и
 * `Y.Text.toString()`: для обоих нужен `yjs` значением, а сюда он не едет.
 *
 * Правила, общие всем трём:
 *
 * - метка и не строки пропускаются;
 * - строка из одних пробелов это незаполненное поле, как у пропусков, но
 *   непустая строка уезжает КАК НАБРАНА, без обрезки краёв: второй этап
 *   адресует выделение смещениями в тексте ответа;
 * - длиннее `TRANSLATE_ANSWER_MAX` обрезается, иначе не разобрался бы весь
 *   снимок урока;
 * - `itemIds` задан: ключи не из этого списка отбрасываются (след правки
 *   заготовки). Не задан: берутся первые `TRANSLATE_MAX_ITEMS` ключей В ПОРЯДКЕ
 *   СОРТИРОВКИ, потому что порядок ключей `Y.Map` у клиентов разный, и
 *   обрезка по нему дала бы разное у разных читателей.
 */
export function translateLinesOf(
  entries: Iterable<readonly [string, unknown]>,
  itemIds?: Iterable<string>,
): Record<string, string> | null {
  const known = itemIds === undefined ? null : new Set(itemIds);
  const kept: [string, string][] = [];
  for (const [key, raw] of entries) {
    if (key === COLUMN_MARK_KEY) continue;
    if (known !== null && !known.has(key)) continue;
    if (typeof raw !== "string" || raw.trim() === "") continue;
    kept.push([key, raw.length > TRANSLATE_ANSWER_MAX ? raw.slice(0, TRANSLATE_ANSWER_MAX) : raw]);
  }
  if (kept.length === 0) return null;
  kept.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return Object.fromEntries(kept.slice(0, TRANSLATE_MAX_ITEMS));
}

/**
 * Ответы, которые в ЖИВОМ документе лежат обычным значением и переписываются
 * целиком.
 *
 * ⚠️ Тип нужен затем, чтобы писатель ответа не мог принять `input` и `blanks`.
 * Эти два в живом документе существуют вложенными типами (`Y.Text` и `Y.Map`), а
 * плоский вид у них бывает только в снимке архива. Широкая сигнатура разрешала
 * бы записать плоский `{kind:"blanks", values}` прямо в карту слайда: сборка
 * снимка проглотила бы это обычной веткой разбора, архив выглядел бы
 * правильным, а в документе появились бы 200-300 байт надгробий на КАЖДОЕ
 * нажатие, ради устранения которых `blanks` и переводили на `Y.Map`. У
 * `column` и `translate` ровно то же самое, и в список они не входят по той же
 * причине.
 *
 * Живёт в общем пакете, а не в модуле записи, потому что читают его двое:
 * писатель в `features/board/answers.ts` и виджет в ленивом чанке слоя. Два
 * одинаковых `Extract` в двух местах однажды разъехались бы.
 */
export type DiscreteAnswer = Extract<
  Answer,
  { kind: "choice" | "match" | "order" | "number" | "expr" }
>;
